import { create } from "zustand";
import { useMemo } from "react";
import type { PatientsListItem } from "../services/patients.ts";
import type {
  GraphPathInsight,
  InsightSummary,
  ExtendedPatientSummary,
  D1NAMOPatientProfile,
  InsulinMeasurement,
  ECGFeatures,
  MealImage,
} from "../types/api.ts";

interface PatientState {
  // Basic patient data
  patients: PatientsListItem[];
  selectedPatientId?: string;

  // Insights and analytics
  summaries: Record<string, InsightSummary>;
  extendedSummaries: Record<string, ExtendedPatientSummary>;
  graphPaths: Record<string, GraphPathInsight[]>;

  // D1NAMO profiles
  patientProfiles: Record<string, D1NAMOPatientProfile>;

  // D1NAMO data caches
  insulinData: Record<string, InsulinMeasurement[]>;
  ecgFeatures: Record<string, ECGFeatures[]>;
  mealImages: Record<string, MealImage[]>;

  // Enhanced patient data with counts and metrics
  patientDataCounts: Record<
    string,
    {
      glucoseReadings: number;
      ecgReadings: number;
      insulinMeasurements: number;
      mealImages: number;
      dataCompletenessScore: number;
    }
  >;

  dataQualityMetrics: Record<
    string,
    {
      glucoseQuality: number;
      ecgQuality: number;
      insulinQuality: number;
      mealImageQuality: number;
      overallQuality: number;
      lastUpdated: string;
    }
  >;

  // Search and filtering state
  searchFilters: {
    query?: string;
    cohort?: "diabetes" | "healthy" | "all";
    gender?: "Male" | "Female" | "Other";
    minAge?: number;
    maxAge?: number;
    hasGlucoseData?: boolean;
    hasEcgData?: boolean;
    hasInsulinData?: boolean;
    hasMealImages?: boolean;
    minDataCompleteness?: number;
  };

  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };

  // UI state
  loading: boolean;
  d1namoLoading: Record<
    string,
    {
      insulin: boolean;
      ecg: boolean;
      mealImages: boolean;
    }
  >;

  // Actions
  setPatients: (patients: PatientsListItem[]) => void;
  selectPatient: (patientId: string) => void;
  setSummary: (patientId: string, summary: InsightSummary) => void;
  setExtendedSummary: (
    patientId: string,
    summary: ExtendedPatientSummary,
  ) => void;
  setGraphPaths: (patientId: string, paths: GraphPathInsight[]) => void;
  setPatientProfile: (patientId: string, profile: D1NAMOPatientProfile) => void;
  setInsulinData: (patientId: string, data: InsulinMeasurement[]) => void;
  setECGFeatures: (patientId: string, data: ECGFeatures[]) => void;
  setMealImages: (patientId: string, data: MealImage[]) => void;
  setLoading: (loading: boolean) => void;
  setD1NAMOLoading: (
    patientId: string,
    dataType: "insulin" | "ecg" | "mealImages",
    loading: boolean,
  ) => void;

  // Enhanced data actions
  setPatientDataCounts: (
    patientId: string,
    counts: {
      glucoseReadings: number;
      ecgReadings: number;
      insulinMeasurements: number;
      mealImages: number;
      dataCompletenessScore: number;
    },
  ) => void;
  updatePatientDataCounts: (
    patientId: string,
    updates: Partial<{
      glucoseReadings: number;
      ecgReadings: number;
      insulinMeasurements: number;
      mealImages: number;
      dataCompletenessScore: number;
    }>,
  ) => void;
  setDataQualityMetrics: (
    patientId: string,
    metrics: {
      glucoseQuality: number;
      ecgQuality: number;
      insulinQuality: number;
      mealImageQuality: number;
      overallQuality: number;
      lastUpdated: string;
    },
  ) => void;
  setSearchFilters: (filters: Partial<PatientState["searchFilters"]>) => void;
  clearSearchFilters: () => void;
  setPagination: (pagination: Partial<PatientState["pagination"]>) => void;

  // Batch operations for D1NAMO data
  batchUpdatePatientCounts: (
    updates: Array<{
      patientId: string;
      counts: {
        glucoseReadings: number;
        ecgReadings: number;
        insulinMeasurements: number;
        mealImages: number;
        dataCompletenessScore: number;
      };
    }>,
  ) => void;

  // Utility actions
  clearPatientData: (patientId: string) => void;
  clearAllData: () => void;
  updatePatientInList: (
    patientId: string,
    updates: Partial<PatientsListItem>,
  ) => void;
}

export const usePatientStore = create<PatientState>((set, _get) => ({
  // Initial state
  patients: [],
  selectedPatientId: undefined,
  summaries: {},
  extendedSummaries: {},
  graphPaths: {},
  patientProfiles: {},
  insulinData: {},
  ecgFeatures: {},
  mealImages: {},
  patientDataCounts: {},
  dataQualityMetrics: {},
  searchFilters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  loading: false,
  d1namoLoading: {},

  // Basic actions
  setPatients: (patients) => set({ patients }),
  selectPatient: (patientId) =>
    set({
      selectedPatientId: patientId,
    }),
  setSummary: (patientId, summary) =>
    set((state) => ({
      summaries: {
        ...state.summaries,
        [patientId]: summary,
      },
    })),
  setExtendedSummary: (patientId, summary) =>
    set((state) => ({
      extendedSummaries: {
        ...state.extendedSummaries,
        [patientId]: summary,
      },
    })),
  setGraphPaths: (patientId, paths) =>
    set((state) => ({
      graphPaths: {
        ...state.graphPaths,
        [patientId]: paths,
      },
    })),

  // D1NAMO profile actions
  setPatientProfile: (patientId, profile) =>
    set((state) => ({
      patientProfiles: {
        ...state.patientProfiles,
        [patientId]: profile,
      },
    })),

  // D1NAMO data actions
  setInsulinData: (patientId, data) =>
    set((state) => ({
      insulinData: {
        ...state.insulinData,
        [patientId]: data,
      },
    })),
  setECGFeatures: (patientId, data) =>
    set((state) => ({
      ecgFeatures: {
        ...state.ecgFeatures,
        [patientId]: data,
      },
    })),
  setMealImages: (patientId, data) =>
    set((state) => ({
      mealImages: {
        ...state.mealImages,
        [patientId]: data,
      },
    })),

  // Loading state actions
  setLoading: (loading) => set({ loading }),
  setD1NAMOLoading: (patientId, dataType, loading) =>
    set((state) => ({
      d1namoLoading: {
        ...state.d1namoLoading,
        [patientId]: {
          ...state.d1namoLoading[patientId],
          [dataType]: loading,
        },
      },
    })),

  // Enhanced data actions
  setPatientDataCounts: (patientId, counts) =>
    set((state) => ({
      patientDataCounts: {
        ...state.patientDataCounts,
        [patientId]: counts,
      },
    })),

  updatePatientDataCounts: (patientId, updates) =>
    set((state) => ({
      patientDataCounts: {
        ...state.patientDataCounts,
        [patientId]: {
          ...state.patientDataCounts[patientId],
          ...updates,
        },
      },
    })),

  setDataQualityMetrics: (patientId, metrics) =>
    set((state) => ({
      dataQualityMetrics: {
        ...state.dataQualityMetrics,
        [patientId]: metrics,
      },
    })),

  setSearchFilters: (filters) =>
    set((state) => ({
      searchFilters: {
        ...state.searchFilters,
        ...filters,
      },
    })),

  clearSearchFilters: () =>
    set({
      searchFilters: {},
    }),

  setPagination: (pagination) =>
    set((state) => ({
      pagination: {
        ...state.pagination,
        ...pagination,
      },
    })),

  // Batch operations for D1NAMO data
  batchUpdatePatientCounts: (updates) =>
    set((state) => {
      const newCounts = { ...state.patientDataCounts };
      updates.forEach(({ patientId, counts }) => {
        newCounts[patientId] = counts;
      });
      return {
        patientDataCounts: newCounts,
      };
    }),

  // Utility actions
  clearPatientData: (patientId) =>
    set((state) => {
      const newState = { ...state };
      delete newState.summaries[patientId];
      delete newState.extendedSummaries[patientId];
      delete newState.graphPaths[patientId];
      delete newState.patientProfiles[patientId];
      delete newState.insulinData[patientId];
      delete newState.ecgFeatures[patientId];
      delete newState.mealImages[patientId];
      delete newState.patientDataCounts[patientId];
      delete newState.dataQualityMetrics[patientId];
      delete newState.d1namoLoading[patientId];
      return newState;
    }),

  clearAllData: () =>
    set({
      patients: [],
      selectedPatientId: undefined,
      summaries: {},
      extendedSummaries: {},
      graphPaths: {},
      patientProfiles: {},
      insulinData: {},
      ecgFeatures: {},
      mealImages: {},
      patientDataCounts: {},
      dataQualityMetrics: {},
      searchFilters: {},
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      },
      loading: false,
      d1namoLoading: {},
    }),

  updatePatientInList: (patientId, updates) =>
    set((state) => ({
      patients: state.patients.map((patient) =>
        patient.patientId === patientId ? { ...patient, ...updates } : patient,
      ),
    })),
}));

// Selectors for common use cases
export const useSelectedPatient = () => {
  const selectedPatientId = usePatientStore((state) => state.selectedPatientId);
  const patients = usePatientStore((state) => state.patients);
  return patients.find((patient) => patient.patientId === selectedPatientId);
};

export const usePatientD1NAMOData = (patientId?: string) => {
  const insulinData = usePatientStore(
    (state) => state.insulinData[patientId || ""],
  );
  const ecgFeatures = usePatientStore(
    (state) => state.ecgFeatures[patientId || ""],
  );
  const mealImages = usePatientStore(
    (state) => state.mealImages[patientId || ""],
  );
  const patientProfile = usePatientStore(
    (state) => state.patientProfiles[patientId || ""],
  );
  const extendedSummary = usePatientStore(
    (state) => state.extendedSummaries[patientId || ""],
  );

  return {
    insulinData: insulinData || [],
    ecgFeatures: ecgFeatures || [],
    mealImages: mealImages || [],
    patientProfile,
    extendedSummary,
    hasData: {
      insulin: (insulinData || []).length > 0,
      ecg: (ecgFeatures || []).length > 0,
      mealImages: (mealImages || []).length > 0,
      profile: !!patientProfile,
    },
  };
};

export const usePatientLoadingStates = (patientId?: string) => {
  const loading = usePatientStore((state) => state.loading);
  const d1namoLoading = usePatientStore(
    (state) => state.d1namoLoading[patientId || ""],
  );

  return {
    globalLoading: loading,
    insulinLoading: d1namoLoading?.insulin || false,
    ecgLoading: d1namoLoading?.ecg || false,
    mealImagesLoading: d1namoLoading?.mealImages || false,
    anyD1NAMOLoading: d1namoLoading
      ? Object.values(d1namoLoading).some(Boolean)
      : false,
  };
};

// Enhanced selectors for D1NAMO data
export const usePatientDataCounts = (patientId?: string) => {
  return usePatientStore((state) => state.patientDataCounts[patientId || ""]);
};

export const usePatientDataQuality = (patientId?: string) => {
  return usePatientStore((state) => state.dataQualityMetrics[patientId || ""]);
};

export const usePatientSearchFilters = () => {
  return usePatientStore((state) => state.searchFilters);
};

export const usePatientPagination = () => {
  return usePatientStore((state) => state.pagination);
};

// Combined selector for patient data overview
export const usePatientOverview = (patientId?: string) => {
  const patient = useSelectedPatient();
  const dataCounts = usePatientDataCounts(patientId);
  const dataQuality = usePatientDataQuality(patientId);
  const d1namoData = usePatientD1NAMOData(patientId);

  return {
    patient,
    dataCounts,
    dataQuality,
    d1namoData,
    hasData: {
      glucose: (dataCounts?.glucoseReadings || 0) > 0,
      ecg: (dataCounts?.ecgReadings || 0) > 0,
      insulin: (dataCounts?.insulinMeasurements || 0) > 0,
      mealImages: (dataCounts?.mealImages || 0) > 0,
    },
    dataCompleteness: dataCounts?.dataCompletenessScore || 0,
    overallQuality: dataQuality?.overallQuality || 0,
  };
};

// Additional utility selectors for D1NAMO data
export const usePatientsWithRichData = () => {
  const patients = usePatientStore((state) => state.patients);
  const dataCounts = usePatientStore((state) => state.patientDataCounts);

  return patients.filter((patient) => {
    const counts = dataCounts[patient.patientId];
    if (!counts) return false;
    const total =
      counts.glucoseReadings +
      counts.ecgReadings +
      counts.insulinMeasurements +
      counts.mealImages;
    return total > 1000; // Rich data threshold
  });
};

export const usePatientDataSummary = () => {
  const patients = usePatientStore((state) => state.patients);
  const dataCounts = usePatientStore((state) => state.patientDataCounts);

  return useMemo(() => {
    const summary = {
      totalPatients: patients.length,
      withGlucoseData: 0,
      withEcgData: 0,
      withInsulinData: 0,
      withMealData: 0,
      totalGlucoseReadings: 0,
      totalEcgReadings: 0,
      totalInsulinMeasurements: 0,
      totalMealImages: 0,
      avgDataCompleteness: 0,
    };

    let totalCompleteness = 0;

    patients.forEach((patient) => {
      const counts = dataCounts[patient.patientId];
      if (counts) {
        if (counts.glucoseReadings > 0) {
          summary.withGlucoseData++;
          summary.totalGlucoseReadings += counts.glucoseReadings;
        }
        if (counts.ecgReadings > 0) {
          summary.withEcgData++;
          summary.totalEcgReadings += counts.ecgReadings;
        }
        if (counts.insulinMeasurements > 0) {
          summary.withInsulinData++;
          summary.totalInsulinMeasurements += counts.insulinMeasurements;
        }
        if (counts.mealImages > 0) {
          summary.withMealData++;
          summary.totalMealImages += counts.mealImages;
        }
        totalCompleteness += counts.dataCompletenessScore;
      }
    });

    summary.avgDataCompleteness =
      patients.length > 0 ? Math.round(totalCompleteness / patients.length) : 0;

    return summary;
  }, [patients, dataCounts]);
};
